import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons";
import { faCircleNodes } from "@fortawesome/pro-regular-svg-icons/faCircleNodes";
import { faCommentQuestion } from "@fortawesome/pro-regular-svg-icons/faCommentQuestion";
import { faMagnifyingGlassChart } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlassChart";
import { faMessagePen } from "@fortawesome/pro-regular-svg-icons/faMessagePen";
import { faPeopleGroup } from "@fortawesome/pro-regular-svg-icons/faPeopleGroup";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import { faMobileScreenButton } from "@fortawesome/pro-solid-svg-icons/faMobileScreenButton";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Grid, IconButton, styled, Typography } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcButton, MprcIconButton, MprcIconFa } from "components";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { PublicationType, SavePublicationResultDto, UserPhoneType, UserProfileDto, UserStatus } from "generated/CoreApi_v1";
import { useSession } from "providers/SessionProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DashboardPaths } from "routes/DashboardRouter";
import { UserPanelItem } from "views/dashboard/UserPanel";
import { NewDirectMessageDialog } from "views/foundation/directMessage/NewDirectMessageDialog";
import { SubordinatesOrgDialog } from "views/foundation/user/dialogs/SubordinatesOrgDialog";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { IOpenForm } from "views/knowledge/feed/FeedUserProfile";
import { ActionUserProfileButton, BackgroundImageButton, BlockedUserProfileButton, EditProfileButton, FollowUserProfileButton, ShareUserProfileButton, SilencedUserProfileButton, ToggleMutedUserProfileButton, UnfollowUserProfileButton } from "views/knowledge/feed/FeedUserProfile/defaults";
import { UserProfileRequestConnectionDialog } from "views/knowledge/feed/FeedUserProfile/UserProfileRequestConnectionDialog";


const MainContainer = styled("div",
    { shouldForwardProp: (propName: string) => !["imageUrl", "deleted"].includes(propName) }
)<{ imageUrl: string; deleted: boolean; }>(
    ({ theme, imageUrl, deleted }) => ({
        width: "100%",
        height: deleted ? "unset" : "216px",
        minWidth: "718px",
        position: "relative",
        backgroundImage: `url("${imageUrl}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        borderRadius: "16px",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

const ContentContainer = styled("div",
    { shouldForwardProp: (propName: string) => !["deleted"].includes(propName) }
)<{ deleted: boolean; }>(
    ({ theme, deleted }) => ({
        width: "100%",
        height: deleted ? "unset" : "216px",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "#fff",
        borderRadius: "16px",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

interface IProps {
    user: UserProfileDto | null;
    onRigthPanelButtonClick?: () => void;
    onReset: (user?: UserProfileDto) => void;
    onEdit: (user: IOpenForm) => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Cabeçalho do Feed de Perfil do Usuário.
 * @param props
 */
export const FeedUserProfileHeader: React.FC<IProps> = (props) => {
    const user = props.user!;
    const { onRigthPanelButtonClick } = props;
    const session = useSession();
    const navigate = useNavigate();
    const { t } = useTranslation("knowledge");

    const [dialog, setDialog] = useState<any>();

    ///
    const handleClose = (newPub: SavePublicationResultDto | null) => {
        setDialog(null);
    };

    ///
    const handleQuestion = () => {
        setDialog(publicationFactory(
            null,
            PublicationType.Question,
            handleClose,
            { responderId: user.userId, responderName: user.name }
        ));
    };

    ///
    const handleTeam = () => {
        setDialog(
            <SubordinatesOrgDialog
                userId={user.userId}
                onClose={() => setDialog(null)}
            />
        );
    };

    ///
    const handleViewAnalysis = () => {
        navigate(DashboardPaths.UserPanel, { state: { user: { userId: user.userId, name: user.name, thumbnailUrl: user.thumbnailUrl } as UserPanelItem } });
    };

    ///
    const handleRequestConnection = () => {
        setDialog(
            <UserProfileRequestConnectionDialog
                userId={user.userId}
                name={user.name}
                onClose={(saved) => {
                    if (saved) props.onReset();
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const handleDirectMessage = () => {
        setDialog(
            <NewDirectMessageDialog
                recipients={[{ key: user.userId, label: user.name, thumbnailUrl: user.thumbnailUrl } as AutoCompleteDataModel]}
                onClose={() => setDialog(null)}
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderIconText = (text: string, icon: IconDefinition) => {
        return (
            <Grid container spacing={1}>
                <Grid>
                    <Typography variant="subtitle1" color="white">
                        <FontAwesomeIcon icon={icon} color="white" />
                    </Typography>
                </Grid>
                <Grid size="grow">
                    <Typography variant="subtitle1" color="white">{text}</Typography>
                </Grid>
            </Grid>
        );
    };

    const CaptionBullets = (text: string) => {
        return (
            <Typography component="p" variant="subtitle1" color="white" ml={2}
                sx={{
                    listStyleType: "disc",
                    display: "list-item",
                }}>
                <b>{text}</b>
            </Typography>
        );
    };

    const deleted = user.status == UserStatus.Deleted;

    return (
        <>
            {dialog}
            <MainContainer
                imageUrl={user.backgroundImage?.url != null
                    ? user.backgroundImage.url
                    : AppImages.UserProfileBackground
                }
                deleted
            >
                <ContentContainer deleted>
                    <Grid container direction="column" padding={2} wrap="nowrap" width="100%">
                        <Grid>
                            <Grid container spacing={3} mb={1} wrap="nowrap" width="100%">
                                <Grid>
                                    <Avatar
                                        src={
                                            user?.thumbnailUrl
                                                ? user.thumbnailUrl
                                                : AppImages.UserThumbnail
                                        }
                                        title={user.name}
                                        sx={{ marginLeft: "16px", width: "135px", height: "135px" }}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <Grid container alignItems="start" rowSpacing={1} width="100%">
                                        <Grid size={{ xs: 12 }}>
                                            <Grid container width="100%" alignItems="center" >
                                                <Grid size="grow">
                                                    <Typography variant="h5" fontSize="18px" color="white">{user?.name}</Typography>
                                                    {(user.status == UserStatus.Inactive || user.status == UserStatus.Deleted) &&
                                                        <Grid sx={{ mt: 1 }}>
                                                            <Typography variant="caption" color="error" textTransform="uppercase">{user.statusName}</Typography>
                                                        </Grid>
                                                    }
                                                </Grid>
                                                {
                                                    onRigthPanelButtonClick && !deleted &&
                                                    <Grid>
                                                        <Typography color="white">
                                                            <IconButton onClick={onRigthPanelButtonClick} color="inherit" >
                                                                <FontAwesomeIcon icon={faRectangleVerticalHistory} />
                                                            </IconButton>
                                                        </Typography>
                                                    </Grid>
                                                }
                                            </Grid>
                                        </Grid>
                                        {
                                            !deleted &&
                                            <Grid size={{ xs: 12 }}>
                                                <Grid container spacing={1} alignItems="center">
                                                    {user?.units?.map((item, index) => {
                                                        return (
                                                            <Grid key={item.unitId}>
                                                                {
                                                                    index == 0
                                                                        ?
                                                                        <Typography variant="subtitle1" component="p" color="white"><b>{item.name}</b></Typography>
                                                                        :
                                                                        CaptionBullets(item.name)
                                                                }
                                                            </Grid>
                                                        );
                                                    })
                                                    }
                                                </Grid>
                                            </Grid>
                                        }
                                        {
                                            !deleted &&
                                            <Grid>
                                                {user?.workplaceText != undefined && user?.workplaceText != null && user?.workplaceText.length > 0 && RenderIconText(user.workplaceText, faLocationDot)}
                                                {user?.email != undefined && user?.email.length > 0 && RenderIconText(user.email, faEnvelope)}
                                                {user?.phones != undefined && user.phones.length > 0 && (
                                                    <Grid container columnSpacing={2}>
                                                        {user.phones.map((item, index) => (
                                                            <Grid key={index}>
                                                                {RenderIconText(
                                                                    item.phoneNumber,
                                                                    item.phoneType === UserPhoneType.Mobile ? faMobileScreenButton : faPhone
                                                                )}
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                )}
                                            </Grid>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {
                            !deleted &&
                            <Grid>
                                <Grid container justifyContent="space-between" padding={1}>
                                    <Grid>
                                        <Box sx={{
                                            minWidth: "135px",
                                            display: "flex",
                                            justifyContent: "center",
                                        }}>
                                            {
                                                user?.isCurrentUser
                                                    ? <EditProfileButton />
                                                    : user?.blockedOn != null
                                                        ?
                                                        <BlockedUserProfileButton user={user} onSuccess={(user) => props.onReset(user)} />
                                                        : user?.silencedOn != null
                                                            ? <SilencedUserProfileButton user={user} onSuccess={(user) => props.onReset(user)} />
                                                            : user?.isFollowing
                                                                ?
                                                                <>
                                                                    <UnfollowUserProfileButton user={user} onSuccess={() => props.onReset({ ...user, isFollowing: false })} />
                                                                    <Box sx={{ display: "flex", justifyContent: "center", minWidth: "50px" }}>
                                                                        <ToggleMutedUserProfileButton
                                                                            user={user}
                                                                            onSuccess={() => props.onReset({ ...user, muted: !user!.muted })}
                                                                        />
                                                                    </Box>
                                                                </>
                                                                :
                                                                <FollowUserProfileButton user={user} onSuccess={() => props.onReset({ ...user, isFollowing: true })} />
                                            }
                                        </Box>
                                    </Grid>
                                    {
                                        user.blockedOn == null && session.user.userId != user.userId &&
                                        <Grid>
                                            <MprcButton
                                                startIcon={<FontAwesomeIcon icon={faCommentQuestion} />}
                                                size="large"
                                                text={t("Feed.UserProfile.QuestionButtonText")}
                                                title={t("Feed.UserProfile.QuestionButtonTitle")}
                                                sx={{ color: "white" }}
                                                onClick={handleQuestion}
                                            />
                                        </Grid>
                                    }
                                    {
                                        user.blockedOn == null && session.user.userId != user.userId &&
                                        <Grid>
                                            <MprcButton
                                                startIcon={<FontAwesomeIcon icon={faMessagePen} />}
                                                size="large"
                                                text={t("Feed.UserProfile.DirectMessageText")}
                                                title={t("Feed.UserProfile.DirectMessageTitle")}
                                                sx={{ color: "white" }}
                                                onClick={handleDirectMessage}
                                            />
                                        </Grid>
                                    }
                                    {
                                        user.allowRequestConnection &&
                                        <Grid>
                                            <MprcButton
                                                startIcon={<FontAwesomeIcon icon={faCircleNodes} />}
                                                size="large"
                                                text={t("Feed.UserProfile.RequestConnectionButtonText")}
                                                title={t("Feed.UserProfile.RequestConnectionButtonTitle")}
                                                sx={{ color: "white" }}
                                                onClick={handleRequestConnection}
                                            />
                                        </Grid>
                                    }
                                    {
                                        user.allowTeam && (user.isManager || user.managerId != null) &&
                                        <Grid>
                                            <MprcButton
                                                startIcon={<FontAwesomeIcon icon={faPeopleGroup} />}
                                                size="large"
                                                sx={{ color: "white" }}
                                                onClick={handleTeam}
                                            >
                                                {user.isCurrentUser ? t("Feed.UserProfile.YourTeamButton") : t("Feed.UserProfile.TeamButtonText")}
                                            </MprcButton>
                                        </Grid>
                                    }
                                    <Grid>
                                        <Grid container columnSpacing={2}>
                                            {
                                                user.allowViewAnalysis &&
                                                <Grid>
                                                    <MprcIconButton
                                                        title={t("Feed.UserProfile.ButtonAnalisysTitle")}
                                                        color="paper"
                                                        onClick={handleViewAnalysis}
                                                    >
                                                        <MprcIconFa icon={faMagnifyingGlassChart} />
                                                    </MprcIconButton>
                                                </Grid>
                                            }
                                            {
                                                user.isCurrentUser &&
                                                <Grid>
                                                    <BackgroundImageButton user={user} onSuccess={(userUpdated) => props.onReset(userUpdated)} />
                                                </Grid>
                                            }
                                            <Grid>
                                                <ShareUserProfileButton user={user} />
                                            </Grid>
                                            <Grid>
                                                <ActionUserProfileButton user={user} onSuccess={(userUpdated) => props.onReset(userUpdated)} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </ContentContainer>
            </MainContainer>
        </>
    );
};
